import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/index.js';
import { uuid } from '../../db/ids.js';
import { liveDecks, deckPath } from '../../db/repo/decks.js';
import { createNote, updateNote, deleteNotes, restoreNotes } from '../../db/repo/notes.js';
import { getCardType, cardCount } from '../../cardTypes/index.js';
import { toText } from '../../editor/content.js';
import { cardStateName, formatInterval } from '../../scheduler/fsrs.js';
import PageHeader from '../../components/PageHeader.jsx';
import EmptyState from '../../components/EmptyState.jsx';
import Button from '../../components/Button.jsx';
import Icon from '../../components/Icon.jsx';
import HelpButton from '../../components/HelpButton.jsx';
import { toast } from '../../components/Toaster.jsx';
import { useSettings, haptic } from '../../app/settingsStore.js';
import { useNow } from '../../hooks/useNow.js';
import NoteForm from './NoteForm.jsx';
import styles from './NoteEditorPage.module.scss';

const LAST_KEY = 'recall.lastAdd';
const DRAFT_KEY = 'recall.draft';

const STATE_TEXT = {
  new: 'Not studied yet',
  learning: 'Learning',
  review: 'Learned',
  suspended: 'Paused',
};

function readJSON(key, fallback) {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return v && typeof v === 'object' ? v : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked (private mode): drafts are a convenience, never required.
  }
}

function hasContent(draft) {
  if (!draft?.fields) return false;
  return Object.values(draft.fields).some((v) =>
    Array.isArray(v) ? v.some((c) => String(c?.text ?? '').trim()) : toText(v).trim(),
  );
}

function CardList({ cards, type }) {
  const now = useNow();
  const def = getCardType(type);
  if (!cards.length) return null;
  return (
    <section className={styles.cards}>
      <h2>{cards.length === 1 ? 'Its card' : `Its ${cards.length} cards`}</h2>
      <ul>
        {cards.map((c) => {
          const state = cardStateName(c);
          const due = c.due - now;
          return (
            <li key={c.id}>
              <span className={styles.cardName}>{def.cardLabel(c.ord)}</span>
              <span className={styles[state]}>{STATE_TEXT[state] ?? state}</span>
              <span className={styles.due}>
                {state === 'new' || state === 'suspended' ? '' : due <= 0 ? 'Due now' : `Next in ${formatInterval(due)}`}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function NoteEditor({ noteId, onDone, embedded = false }) {
  const data = useLiveQuery(
    async () => {
      const [note, decks, cards] = await Promise.all([
        db.notes.get(noteId),
        db.decks.toArray(),
        db.cards.where('noteId').equals(noteId).toArray(),
      ]);
      return { note: note ?? null, decks, cards: cards.filter((c) => !c.deletedAt).sort((a, b) => a.ord - b.ord) };
    },
    [noteId],
  );

  if (!data) return null;
  const { note, decks, cards } = data;
  if (!note || note.purged) {
    return (
      <EmptyState icon="info" title="Card not found">
        It may have been deleted on another device.
      </EmptyState>
    );
  }

  const save = async (values) => {
    await updateNote(db, note.id, { deckId: values.deckId, fields: values.fields, tags: values.tags });
    haptic(10);
    toast.success('Saved');
    onDone?.();
  };

  const remove = async () => {
    await deleteNotes(db, [note.id]);
    toast.info('Moved to trash', {
      action: { label: 'Undo', onClick: () => restoreNotes(db, [note.id]) },
    });
    onDone?.(true);
  };

  return (
    <div className={styles.editor}>
      {note.deletedAt ? (
        <div className={styles.deleted}>
          <Icon name="trash" size={20} />
          <span>This card is in the trash.</span>
          <Button size="sm" variant="primary" onClick={() => restoreNotes(db, [note.id])}>
            Restore
          </Button>
        </div>
      ) : null}
      <NoteForm
        mode="edit"
        key={note.id}
        initial={note}
        decks={decks}
        draftId={note.id}
        existingOrds={cards.map((c) => c.ord)}
        onSubmit={save}
        onDelete={note.deletedAt ? undefined : remove}
        guard={!embedded}
        embedded={embedded}
      />
      {embedded ? null : <CardList cards={cards} type={note.type} />}
    </div>
  );
}

/** One add-a-card session: a fresh draft id per card, so a double tap can never save twice. */
function AddSession({ decks, draft, onAdded }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const afterAdd = useSettings((s) => s.afterAdd);
  const draftTimer = useRef(null);
  const [session, setSession] = useState(() => ({
    key: 0,
    draftId: draft?.id ?? uuid(),
    restored: hasContent(draft),
  }));

  const initial = useMemo(() => {
    const last = readJSON(LAST_KEY, {});
    const fromDraft = session.key === 0 && session.restored ? draft : null;
    const live = liveDecks(decks);
    const wanted = params.get('deck') ?? fromDraft?.deckId ?? last.deckId;
    return {
      type: params.get('type') ?? fromDraft?.type ?? last.type ?? 'basic',
      deckId: live.some((d) => d.id === wanted) ? wanted : live[0]?.id,
      fields: fromDraft?.fields ?? {},
      tags: fromDraft?.tags ?? (Array.isArray(last.tags) ? last.tags : []),
    };
  }, [session.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveDraft = useCallback(
    (values) => {
      clearTimeout(draftTimer.current);
      draftTimer.current = setTimeout(() => writeJSON(DRAFT_KEY, { ...values, id: session.draftId }), 400);
    },
    [session.draftId],
  );

  const fresh = () => {
    clearTimeout(draftTimer.current);
    writeJSON(DRAFT_KEY, null);
    setSession((s) => ({ key: s.key + 1, draftId: uuid(), restored: false }));
  };

  const submit = async (values) => {
    const id = await createNote(db, values);
    clearTimeout(draftTimer.current);
    writeJSON(DRAFT_KEY, null);
    writeJSON(LAST_KEY, { type: values.type, deckId: values.deckId, tags: values.tags });
    haptic(10);
    onAdded();

    const n = cardCount(values.type, values.fields);
    const where = deckPath(decks, values.deckId);
    toast.success(`${n === 1 ? 'Card' : `${n} cards`} added to ${where}`, {
      action: { label: 'Undo', onClick: () => deleteNotes(db, [id]) },
    });

    if (afterAdd === 'back') {
      if (location.key !== 'default') navigate(-1);
      else navigate(`/deck/${values.deckId}`, { replace: true });
      return;
    }
    setSession((s) => ({ key: s.key + 1, draftId: uuid(), restored: false }));
  };

  return (
    <>
      {session.restored && session.key === 0 ? (
        <div className={styles.restored} role="status">
          <Icon name="draft" size={20} />
          <span>We kept the card you were writing.</span>
          <Button size="sm" onClick={fresh}>
            Start fresh
          </Button>
        </div>
      ) : null}
      <NoteForm
        key={session.key}
        mode="add"
        initial={initial}
        decks={decks}
        draftId={session.draftId}
        onSubmit={submit}
        onChange={saveDraft}
      />
    </>
  );
}

function AddNote({ onAdded }) {
  const boot = useLiveQuery(async () => {
    const decks = await db.decks.toArray();
    const draft = readJSON(DRAFT_KEY, null);
    // A draft whose id is already saved was added before the app closed; don't offer it again.
    const stale = draft?.id ? !!(await db.notes.get(draft.id)) : false;
    return { decks, draft: stale ? null : draft };
  }, []);

  if (!boot) return null;

  if (!liveDecks(boot.decks).length) {
    return (
      <EmptyState
        icon="decks"
        title="First, make a deck"
        action={
          <Button variant="primary" size="lg" icon="plus" to="/?newDeck=1">
            New deck
          </Button>
        }
      >
        Cards live in decks. Create one, then come back to add cards.
      </EmptyState>
    );
  }

  return <AddSession decks={boot.decks} draft={boot.draft} onAdded={onAdded} />;
}

export default function NoteEditorPage() {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const [added, setAdded] = useState(0);
  return (
    <div className={styles.page}>
      <PageHeader
        title={noteId ? 'Edit card' : 'Add a card'}
        back={!!noteId}
        actions={
          <>
            {!noteId && added ? (
              <span className={styles.added} key={added}>
                <Icon name="check" size={16} stroke={3} />
                {added} added
              </span>
            ) : null}
            <HelpButton topic="add" />
          </>
        }
      />
      <div className={styles.body}>
        {noteId ? (
          <NoteEditor key={noteId} noteId={noteId} onDone={(deleted) => (deleted ? navigate(-1) : null)} />
        ) : (
          <AddNote onAdded={() => setAdded((n) => n + 1)} />
        )}
      </div>
    </div>
  );
}
